/**
 * The cross-feature contracts of «Маргиналии» (architecture §3; frozen at gate G0 — BUILD-PLAN §2).
 *
 * Every feature agent compiles against this file. Changing a type here is a contract PR that every consumer listed in
 * BUILD-PLAN §2 approves. Feature agents may add fields to their OWN types (in their own folders).
 *
 * Contents:
 * - §3.6 engine constants, creature DNA, rig and mesh data (creatures §A12, §B2–§B3, §B8, §B9; art §3.2; GDD §4.15);
 * - §3.1 `FeatureModule`; §3.2 screens and router; §3.3 store and save state; §3.4 `GameEvents`;
 * - §3.7 services (rewards, economy, creatures + animation, combat, cards, explore), input and `AppContext`;
 * - reward facts and ids (progression §18.2, GDD §5.2);
 * - debug ids used across owners (GDD §4.15 fixtures, creatures §D2 Kunstkamera ids);
 * - a re-export of every renderer type of `./render` (architecture §3: `contracts.ts` re-exports `render.ts`).
 *
 * Related files: `./anim` (animation types, part 1 generated from creatures), `./render` (renderer types),
 * `./cine/types` (camera/feel types), `./rng`, `./clock`, `./bus`, `./reactive`, `./i18n`, `./math`, `./hash`.
 * Import everything through `src/shared/index.ts` (`import { … } from '../shared'`).
 *
 * Pure types + constants: no DOM, no GPU, no `Date`, no `Math.random` (tests/architecture/purity.test.ts).
 */
import type {
  AnimClass, AnimWorldApi, BeatPlan, ClipFamily, ClipLibrary, PointName, StageState, VariantInput, VariantPick, WeaponMeta,
} from './anim';
import type { CreatureLookData, PropMeshData, QualityTier, Renderer } from './render';
import type { Clock } from './clock';
import type { EventBus } from './bus';
import type { ReadonlySignal, Scope } from './reactive';
import type { Rng } from './rng';
import type { TFunction } from './i18n';

export type * from './render';

// ═════════════════════════════════════════════════════════════════════════════════════════════════════════════════════
// Engine constants (GDD §4.15, C15; architecture §3.6, §6; Acceptance test 13)
// ═════════════════════════════════════════════════════════════════════════════════════════════════════════════════════

/** Palette bones per creature (creatures §B2: 35 core + optional chains). */
export const MAX_BONES = 48;
/** Skinned creature slots (creatures §B9.3): 0 player main · 1 opponent main / boss · 2–9 pets · 10–11 transient pool. */
export const MAX_SLOTS = 12;
/** Props have their own buffer and never take a creature slot. */
export const MAX_PROPS = 32;
/** Triptych sub-panels (art AD-30a, creatures §C9.2). */
export const MAX_SUBPANELS = 3;
/** Weapon-trail samples per actor, newest first on the 120 Hz grid (creatures §C3.11, art §4.16). */
export const TRAIL_SAMPLES = 16;
/** Floor contact patches per actor (quads: front pair, hind pair). */
export const FEET_PER_ACTOR = 2;
/** 24 floor patches, densely packed per frame into `SceneLight.contact` (architecture §5.8). */
export const MAX_CONTACTS = MAX_SLOTS * FEET_PER_ACTOR;
/** Figures allowed to break the miniature frame at once (art §4.10 = GDD §4.15; creatures §C7.8's 3 is overruled). */
export const FRAME_BREAK_CAP = 2;
/** Slot map (architecture §6, creatures §B9.3). `SLOT_PETS` / `SLOT_TRANSIENT` are inclusive ranges. */
export const SLOT_PLAYER = 0, SLOT_OPPONENT = 1, SLOT_PETS = [2, 9] as const, SLOT_TRANSIENT = [10, 11] as const;
/** Bytes per vertex of the creature render mesh (art §3.2): pos f32x3 · pat unorm16x2 · skin uint8x4 · info uint8x4. */
export const CREATURE_VERTEX_BYTES = 24;
/** Bytes per vertex of the inverted-hull outline mesh: pos f32x3 · nrm snorm8x4 · skin uint8x4 (w = regionId). */
export const HULL_VERTEX_BYTES = 20;
/** Bytes of one art §3.2 `CreatureLook` (33 × vec4). */
export const CREATURE_LOOK_BYTES = 528;
/** Raw bytes of a creatures §A12 DNA record (448 bits). */
export const DNA_BYTES = 56;
/** Length of the `M…` share string: 'M' + 90 Crockford base32 chars (creatures §A12.2). */
export const DNA_CODE_LENGTH = 91;
/** Dressed triangle budget per creature incl. hull (art §5.3, creatures §B7.4); bosses: creatures §B9.2. */
export const MAX_CREATURE_TRIS = 2230;

// ═════════════════════════════════════════════════════════════════════════════════════════════════════════════════════
// Creature: DNA, rig, mesh (architecture §3.6; creatures-animation.md §A12 owns the bit layout and every id list)
// ═════════════════════════════════════════════════════════════════════════════════════════════════════════════════════

/** 'cat' | 'goose' | 'hare' | 'snail' | 'dog' | … (creatures §A2.1 roster; index = DNA value). */
export type SpeciesId = string;
/** `content/creature/parts.ts` — every option has `acquisition` (creatures §A13 → progression-economy.md §13.7). */
export type PartId = string;
/** art-direction.md §2.1 token name, e.g. 'umber', 'lapisLight'. */
export type PigmentId = string;

/** Decoded creatures §A12 record (v1, 448 bits). `fields` keys are the §A12.1 field names. */
export interface CreatureDNA {
  /** §A12 `ver`. */
  v: 1;
  /** Read by combat (stat bias, «Природа тела / головы»). */
  bodySpecies: SpeciesId; headSpecies: SpeciesId;
  /** «Род»: 0 «Он», 1 «Она» — grammatical agreement in every text template. */
  pronoun: 0 | 1;
  /** Resolved display name: custom 2–16 letters, one space/hyphen (creatures §A8; GDD C83) or generated. */
  name: string;
  /** u16 micro-variation: vertex wobble hash, breathing phase (creatures §C3.1), presentRng (§C3.14). */
  soulSeed: number;
  /** Every other §A12.1 field (enum index, slider step, palette/dye/tincture id). */
  fields: Readonly<Record<string, number>>;
}
/** Share string: 'M' + 90 Crockford base32 chars (91 total) [+ '.' + base64url(custom name)] (creatures §A12.2).
 *  Challenge charters embed the 56 raw bytes; a custom name travels next to them. */
export interface DnaCodec {
  /** `decode` returns null on a bad CRC-10 / unsupported ver. */
  encode(d: CreatureDNA): string; decode(code: string): CreatureDNA | null;
  /** 56 B. */
  toBytes(d: CreatureDNA): Uint8Array; fromBytes(b: Uint8Array): CreatureDNA | null;
}

/** creatures §B2 core skeleton (35 palette bones P0–P34) + optional chains (§B3). ≤ MAX_BONES per creature;
 *  the model-space `root` is the model matrix, not a palette bone. Chain names are creatures'; the ones below are reserved. */
export type BipedBoneName =
  | 'pelvis' | 'spine_01' | 'spine_02' | 'belly' | 'neck' | 'head' | 'jaw' | 'mouth' | 'nose'
  | 'eye_L' | 'eye_R' | 'lid_L' | 'lid_R' | 'brow_L' | 'brow_R' | 'ear_L_01' | 'ear_L_02' | 'ear_R_01' | 'ear_R_02'
  | 'clavicle_L' | 'upperarm_L' | 'forearm_L' | 'hand_L' | 'clavicle_R' | 'upperarm_R' | 'forearm_R' | 'hand_R'
  | 'thigh_L' | 'calf_L' | 'foot_L' | 'toe_L' | 'thigh_R' | 'calf_R' | 'foot_R' | 'toe_R'
  | `tail_${1 | 2 | 3 | 4 | 5}` | 'shell' | `cloth_${number}` | 'wing_L' | 'wing_R' | 'weapon_1' | 'weapon_2' | 'beard' | 'hat_1';
/** creatures §B9.2 `squid` boss rig (32 bones): head, eye_L/R, lid_L/R reuse biped names; the rest are below. */
export type SquidBoneName = 'mantle_01' | 'mantle_02' | 'siphon' | `arm${0 | 1 | 2 | 3 | 4 | 5 | 6 | 7}_${1 | 2 | 3}`;
/** Pet rigs (creatures §B9) reuse biped names where the bone exists (pelvis, spine_01/02, neck, head, jaw, ear_*_01,
 *  tail_N) and prefix every other bone with `pet_` ('pet_leg_FL_2', 'pet_neck_3', 'pet_stalk_L_2'); creatures §B9 owns the list. */
export type BoneName = BipedBoneName | SquidBoneName | `pet_${string}`;
/** CPU-only attachment frames (creatures §B2 «S» rows). */
export type SocketName = 'hat' | 'prop_L' | 'prop_R' | 'back' | 'belt_L' | 'belt_R' | 'chest';
/** Rig families (creatures §B9, §B9.2). */
export type RigKind = 'biped' | 'quad' | 'hopper' | 'bird' | 'snail' | 'squire' | 'swarm' | 'squid';

/** One palette bone. Bind orientation = identity (creatures §B1); `pivot` = model-space bind position (H). */
export interface BoneDef { name: BoneName; parent: number /* -1 = root */; pivot: [number, number, number] /* model-space bind position (H); bind orientation = identity (creatures §B1) */ }
/** A creature skeleton. */
export interface Rig {
  kind: RigKind;
  /** Parents before children; length ≤ MAX_BONES. */
  bones: BoneDef[];
  /** Species may omit bones (e.g. no tail). */
  index: Partial<Record<BoneName, number>>;
  /** creatures §B2 «S» rows (CPU-only frames); pets have few. */
  sockets: Partial<Record<SocketName, { bone: number; offset: [number, number, number]; rot: [number, number, number] }>>;
  /** Chains made rigid by the palette cap (creatures §B3) — QA warning, never a crash. */
  rigidChains: string[];
}

/** CPU-built creature mesh (art §3.2 layouts; GDD C16). */
export interface CreatureMeshData {
  /** Render mesh, NON-indexed, 24 B/vertex (art §3.2; GDD C16): pos f32x3 · pat unorm16x2 · skin uint8x4 (boneA, boneB,
   *  weightA, lineWeight) · info uint8x4 (materialId, regionId, edgeMask, corner). */
  vertices: ArrayBuffer;
  vertexCount: number;
  /** Inverted-hull outline mesh: 20 B vertices (pos f32x3 · nrm snorm8x4 · skin uint8x4 (boneA, boneB, weightA,
   *  regionId — r3, for the skull smear factor)), u16 indices. */
  hull: { vertices: ArrayBuffer; indices: Uint16Array; vertexCount: number; indexCount: number };
  /** Rest pose, H. */
  bounds: { min: [number, number, number]; max: [number, number, number] };
  /** Rest anchors (weapon points come from WeaponMeta). */
  points: Partial<Record<PointName, { bone: number; pos: [number, number, number] }>>;
  /** 'hero' = creatures §B9 hero LOD for ECU/CU shots and constructor head stages. */
  lod: 0 | 1 | 'hero';
  /** Dressed total ≤ 2,230 incl. hull (art §5.3, creatures §B7.4); bosses: creatures §B9.2. */
  tris: { render: number; hull: number };
}

// ═════════════════════════════════════════════════════════════════════════════════════════════════════════════════════
// §3.1 Feature plug-in
// ═════════════════════════════════════════════════════════════════════════════════════════════════════════════════════

/** Feature keys; the first segment of every `ScreenId`. */
export type FeatureKey = 'app' | 'creature' | 'combat' | 'explore' | 'cards' | 'meta' | 'debug';
/** e.g. 'combat/arena', 'explore/road' (GDD §4.5 is the only screen list). */
export type ScreenId = `${FeatureKey}/${string}`;

/**
 * A feature plugs into the app through one of these (`src/app/features.ts` is the only central list:
 * `FEATURES = [creature, combat, explore, cards, meta, debug]`).
 */
export interface FeatureModule {
  key: FeatureKey;
  /** Save slices this feature owns (§4). */
  slices?: SliceDef<any>[];
  screens?: ScreenDef<any>[];
  /** Register services, reward sinks, offline catch-up. */
  onBoot?(ctx: AppContext): void | Promise<void>;
  /** Namespace → members, mounted at window.__marg[ns] (GDD §4.15: creature → creature + anim; meta → econ;
   *  cards → cards + snails + dice; debug → render + ui). */
  debugApi?(ctx: AppContext): Record<string, Record<string, unknown>>;
}

// ═════════════════════════════════════════════════════════════════════════════════════════════════════════════════════
// §3.2 Screens and router
// ═════════════════════════════════════════════════════════════════════════════════════════════════════════════════════

export type ScreenParams = Record<string, string | number | boolean | undefined>;

/** GDD §4.5 "Kind" column (F0 extension, optional): how the router and the shell present a screen. */
export type ScreenKind = 'boot' | 'stage' | 'immersive' | 'tabRoot' | 'page' | 'sheet' | 'sheetPage' | 'overlay' | 'debug';
/** Slice scope of a screen (GDD §10; F0 extension, optional). */
export type ScreenScope = 'P0' | 'P0-lite' | 'P1' | 'P2';
/** The five tab roots (ui-ux §3; GDD §4.5 tab bar order). */
export type TabId = 'life' | 'road' | 'arena' | 'cards' | 'order';

export interface ScreenDef<P extends ScreenParams = ScreenParams> {
  id: ScreenId;
  /** Hash route pattern from GDD §4.5, e.g. '#/arena/fight/:fightId'; `[/:x]` marks optional segments
   *  ('#/life[/:tab[/:sub]]'); '#duel=:code' for the charter. Absent = overlay or debug-only screen. */
  route?: string;
  /** Short names for the debug entry: ['arena'] → ?screen=arena → #/arena. */
  aliases?: string[];
  /** String key for document.title and the a11y heading. */
  titleKey: string;
  /** false → renderer.setScene({ kind: 'none' }) (parchment clear, idle). */
  needs3d: boolean;
  /** Validate + defaults; unknown params ignored, bad values → defaults + console.warn. */
  parseParams(q: URLSearchParams): P;
  /** For history/deep links. */
  toQuery?(p: P): Record<string, string>;
  /** Used by scripts/capture.mjs. `at` = presentation ms. */
  capture?: Array<{ label: string; params: P; at?: number /* presentation ms */ }>;
  create(ctx: ScreenContext, params: P): Screen;
  /** F0 extension (optional): GDD §4.5 kind. */
  kind?: ScreenKind;
  /** F0 extension (optional): slice scope (P1/P2 screens are registered as stubs only). */
  scope?: ScreenScope;
  /** F0 extension (optional): the tab this screen belongs to (tab bar highlight). */
  tab?: TabId;
}

export interface Screen {
  /** Router mounts it into #ui. */
  readonly root: HTMLElement;
  /** Transition in (≤ 350 ms; instant under reducedMotion). */
  enter?(from: ScreenId | null): void | Promise<void>;
  /** Logic tick (SIM_DT); not called while paused unless stepped (§2.3). */
  update?(dtMs: number): void;
  /** Once per rAF at the exact display instant: presentation only. */
  render?(presentMs: number, realDtMs: number): void;
  resize?(vp: Viewport): void;
  /** true = handled (close sheet/modal), false = router pops. */
  onBack?(): boolean;
  exit?(to: ScreenId): void | Promise<void>;
  /** Must release DOM, subscriptions, creature handles. */
  dispose(): void;
  /** Captures wait for true (no tween/transition/streaming text in flight). */
  isIdle?(): boolean;
  /** Exposed in __marg.getState().screen.debug. */
  debugState?(): unknown;
}

/** CSS viewport + device pixel ratio + safe-area insets (CSS px). */
export interface Viewport { cssW: number; cssH: number; dpr: number; portrait: boolean; safe: { t: number; r: number; b: number; l: number } }

export interface ScreenContext extends AppContext {
  params: ScreenParams;
  /** ctx.rng.stream(`screen:${id}`) — deterministic per session seed. */
  rng: Rng;
  /** Auto-disposed with the screen (effects, listeners, timers). */
  scope: Scope;
}

/**
 * Hash routes are the source of truth (GDD §4.5, ui-ux §3.3). `?screen=<ScreenId|alias>` (alias `view`) is a debug entry
 * converted to the hash route; overlays push `history.state = { overlay: id }` so back closes the top overlay first.
 */
export interface Router {
  readonly current: { id: ScreenId; params: ScreenParams } | null;
  goto(id: ScreenId | string, params?: ScreenParams, opts?: { replace?: boolean; transition?: 'page' | 'fade' | 'none' }): Promise<void>;
  /** Android back / Esc / browser back → screen.onBack() first. */
  back(): void;
  list(): ScreenDef[];
}

// ═════════════════════════════════════════════════════════════════════════════════════════════════════════════════════
// §3.3 Store and save state (§4 persistence)
// ═════════════════════════════════════════════════════════════════════════════════════════════════════════════════════

/** Save slices; each has exactly one owning feature (architecture §3.3, GDD §4.15). */
export type SliceKey = 'creature' | 'combat' | 'exploration' | 'cards' | 'economy' | 'ui';
/** Every slice key, in envelope order. */
export const SLICE_KEYS: readonly SliceKey[] = ['creature', 'combat', 'exploration', 'cards', 'economy', 'ui'];
/** Which feature folder owns (and alone may `store.update`) each slice. */
export const SLICE_OWNER: Readonly<Record<SliceKey, FeatureKey>> = {
  creature: 'creature', combat: 'combat', exploration: 'explore', cards: 'cards', economy: 'meta', ui: 'app',
};

export interface SaveState {
  /** Envelope schema (app-owned migrations). */
  schema: 1;
  /** Wall ms. */
  createdAt: number; updatedAt: number;
  /** Wall ms, max-monotonic (clock-rollback safe). */
  lastActiveAt: number;
  /** Per-save root seed (§3.5). */
  seed: string;
  /** App version that wrote it. */
  build: string;
  slices: {
    /** creature.md: roster, active creature, DNA. */
    creature: { v: number } & Record<string, unknown>;
    /** combat.md: ladder, records (spec + inputs ≤ 2 KB each). */
    combat: { v: number } & Record<string, unknown>;
    /** exploration.md §25.3 ExploSave. */
    exploration: { v: number } & Record<string, unknown>;
    /** minigames.md: the mini-games subtree { cards, snails, dice } (GDD C12). */
    cards: { v: number } & Record<string, unknown>;
    /** progression-economy.md §18.2 (currencies, items, ledger…). */
    economy: { v: number } & Record<string, unknown>;
    /** ui-ux.md: FTUE step, seen ids for badges, coach marks shown (GDD C11). */
    ui: { v: number } & Record<string, unknown>;
  };
}
/** Device settings in localStorage 'marg.settings.v1' — NOT part of SaveState, never exported (GDD §4.15, C11). Full row list: ui-ux §6.24. */
export interface DeviceSettings { v: 1; lang: 'ru' | 'en'; music: number; sfx: number; motion: 'full' | 'reduced' | 'min'; textScale: 100 | 115 | 130 | 150; quality: 'auto' | QualityTier; haptics: boolean; leftHanded: boolean }

/** One save slice: creation, migrations, validation (architecture §4.4 rules for slice owners). */
export interface SliceDef<T extends { v: number }> {
  key: SliceKey;
  /** Current T['v']. */
  version: number;
  /** Fresh slice. Pure: time comes from `nowWall`, never `Date.now()`. */
  create(seed: string, nowWall: number): T;
  /** fromVersion → next version object. */
  migrations: Record<number, (old: any) => any>;
  /** [] = valid; messages otherwise. */
  validate(x: unknown): string[];
}

/**
 * The app store. `update()` runs the recipe on a `structuredClone` of the slice (copy-on-write), bumps `updatedAt`,
 * notifies, schedules a save; `reason` is logged (`__marg.getState().storeLog`, last 100). **Only the owning feature
 * calls `update()` on its slice** ({@link SLICE_OWNER}; enforced by tests/architecture/purity.test.ts).
 */
export interface Store {
  readonly state: Readonly<SaveState>;
  slice<K extends SliceKey>(key: K): ReadonlySignal<SaveState['slices'][K]>;
  update<K extends SliceKey>(key: K, recipe: (draft: SaveState['slices'][K]) => void, reason: string): void;
  /** Batches notifications + one save. */
  transaction(reason: string, fn: () => void): void;
  /** Load / fixtures / import. */
  replace(next: SaveState, reason: string): void;
  subscribe(fn: (state: SaveState, reason: string) => void): () => void;
}

/** Storage envelope in `marg.save.0/1/2` (architecture §4.1); `checksum` = fnv1a hex of JSON.stringify(data). */
export interface SaveEnvelope { format: 'marg-save'; schema: number; savedAt: number; checksum: string /* fnv1a hex of JSON.stringify(data) */; data: SaveState }
/** localStorage / IndexedDB keys (architecture §4.1, GDD §4.15). */
export const STORAGE_KEYS = {
  save: ['marg.save.0', 'marg.save.1', 'marg.save.2'] as const,
  corruptPrefix: 'marg.save.corrupt.',
  lock: 'marg.lock',
  settings: 'marg.settings.v1',
  telemetry: 'marg.telemetry',
} as const;

// ═════════════════════════════════════════════════════════════════════════════════════════════════════════════════════
// §3.4 Event bus payloads (notifications only; state changes go through services)
// ═════════════════════════════════════════════════════════════════════════════════════════════════════════════════════

export interface GameEvents {
  'app/screen': { from: ScreenId | null; to: ScreenId };
  'app/saved': { bytes: number; reason: string };
  'app/visibility': { hidden: boolean };
  'creature/changed': { creatureId: string };
  'combat/fightStarted': { fightId: string; mode: string };
  'combat/fightEnded': { fightId: string; mode: string; winner: 0 | 1; reason: 'ko' | 'judges' };
  'explore/chronicle': { entryId: string };
  'explore/questDone': { questId: string };
  'cards/matchEnded': { matchId: string; won: boolean };
  'meta/granted': { source: string; ledgerIds: string[] };
  'meta/levelUp': { creatureId: string; level: number };
  'ui/toast': { key: string; params?: Record<string, string | number>; tone?: 'info' | 'reward' | 'warning' };
}

// ═════════════════════════════════════════════════════════════════════════════════════════════════════════════════════
// Rewards: ids, facts, bundle, result (progression §18.2, GDD §5.2 — the matrix rows live in
// src/content/meta/rewardMatrix.ts, P5-M1). No feature computes XP or gold: features pass FACTS to grant().
// ═════════════════════════════════════════════════════════════════════════════════════════════════════════════════════

/** Every `grant()` id: progression §18.2 `ActivityId` ∪ GDD §5.2's `arena.champion` and `cards.dailyPuzzle`. */
export const REWARD_IDS = [
  'arena.tutorial', 'arena.win', 'arena.firstTakings', 'arena.loss', 'arena.judges', 'arena.grudge', 'arena.champion',
  'arena.ladder', 'arena.praise', 'arena.spar', 'arena.friend', 'arena.silence', 'arena.pickup',
  'tourney.daily', 'tourney.grand', 'beast.weekly',
  'explore.travel', 'explore.diligence', 'explore.verb', 'explore.fork', 'explore.glint', 'explore.chapter',
  'explore.deposit', 'order.quest', 'order.tithe', 'order.stamp', 'order.request', 'order.works',
  'dungeon.clear', 'dungeon.tier', 'dungeon.room', 'dungeon.brandExchange', 'beast.tokenExchange',
  'cards.match.win', 'cards.match.loss', 'cards.match.draw', 'cards.blitz.win', 'cards.blitz.loss',
  'cards.puzzle', 'cards.dailyPuzzle', 'cards.circle', 'minigame.other',
  'meta.obedience', 'meta.obedienceSet', 'meta.feat', 'meta.workshop', 'meta.levelUp', 'meta.overflow',
  'meta.deed', 'meta.rebirth', 'season.tier', 'debug',
] as const;
/** A `grant()` source id (a row of `src/content/meta/rewardMatrix.ts`). */
export type RewardId = (typeof REWARD_IDS)[number];

/** Ghost bounty band (GDD §5.7). */
export type BountyBand = 'low' | 'par' | 'high' | 'top';
/** «Зарубка» notch id (progression §9.2); the economy owns the list. */
export type NotchId = string;
/** Road focus id (exploration «Прилежание» focus); exploration owns the list. */
export type FocusId = string;

/** Built only by `rewardsFromFight()` from combat's FightResult (combat §12.7, progression §18.2). */
export interface ArenaFact {
  mode: 'arena' | 'grudge' | 'tournament' | 'friend' | 'spar' | 'beast' | 'tutorial';
  outcome: 'win' | 'judgesWin' | 'loss';
  /** By-name ghosts: bounty clamped to ≤ 1. */
  bounty: 0.9 | 1 | 1.25 | 1.5; bountyBand: BountyBand; byName: boolean;
  vow: 'silence' | 'vigil' | 'humility' | 'fasting' | null; vigilMet: boolean;
  attended: boolean; closeLoss: boolean;
  /** Perfect + riposte + finish omens (gear +2 pp each, ≤ +6). */
  initials: number; beauty: 0 | 1 | 2 | 3; perfectOmens: number;
  oppLevel: number; tutorialStep?: 1 | 2 | 3; notchCut?: NotchId;
}
/** A finished card match / blitz / lesson puzzle (progression §12.2 row). */
export interface CardFact { kind: 'full' | 'blitz' | 'puzzle'; outcome: 'win' | 'loss' | 'draw'; ai: 'novice' | 'brother' | 'prior' | 'abbot'; npcId: string; firstCircleWin: boolean }
/** Non-arena performance facts; the registry turns facts into multipliers (never raw multipliers — lint). */
export type PerfFact =
  | { id: 'judges' } | { id: 'consolation' } | { id: 'watchedFight'; v: 1.1 | 1.25 } | { id: 'perilous' }
  | { id: 'focus'; focus: FocusId };

/** Economy id aliases (the economy owns each registry; progression §7, §6, §13). */
export type CurrencyId = string;
export type DropTableId = string;
export type SlotId = string;
export type DungeonId = string;
export type NamedItemId = string;
export type WeaponId = string;
export type BeastId = string;
export type VirtueId = string;
export type CosmeticId = string;
export type TitleId = string;

/** What a call site hands to `grant()` (progression §18.2 `RewardBundle`). Arena and card sources pass facts only. */
export interface RewardBundle {
  /** Units (×U(level)); forbidden on arena and card sources (they pass facts). */
  xpU?: number;
  /** % of xpToNext (×paceMult(level)); exploration uses this. */
  xpPct?: number;
  goldG?: number; goldCU?: number;
  /** Arena-side sources: bounty, vow, close loss, «Почин», grudge, Vigil, virtues, gear chance. */
  arena?: ArenaFact;
  /** Card sources: the §12.2 row. */
  card?: CardFact;
  /** Never raw multipliers (lint). */
  perf?: PerfFact[];
  currencies?: Partial<Record<CurrencyId, number>>;
  /** Exploration material ids, mapped per progression §7. */
  materials?: { id: string; n: number }[];
  drops?: { table: DropTableId; count: number; iLvl?: number; rarityBias?: number; pickSlot?: SlotId; look?: DungeonId }[];
  /** First-kill named item (§6.6.1); or the first-Киноварная signature Gilded (§6.7). */
  named?: NamedItemId;
  /** Dowry / pickups / chapter unlocks. */
  weapons?: WeaponId[];
  /** Chapter bosses → brands; weekly beasts → tokens. */
  brands?: { dungeon: DungeonId; n: number }; beastToken?: { beast: BeastId };
  quires?: number; virtues?: Partial<Record<VirtueId, number>>;
  /** «Рвение» (order reputation, currency `zeal:<orderId>`, GDD §4.4). Not «Азарт», whose code is `zest` (GDD §4.14). */
  zeal?: { orderId: string; amount: number }; renown?: number; beads?: number;
  cosmetics?: CosmeticId[]; titles?: TitleId[];
  energy?: Partial<Record<'candle' | 'key' | 'grace', number>>;
  /** Source-specific numeric/string parameters (e.g. snail place factor); interpreted by the matrix row. */
  params?: Record<string, number | string>;
}
/** Returned by `grant()` for the shared reward-toast component. */
export interface GrantResult {
  ledgerIds: string[];
  /** Toast/result lines: string keys + params (no pre-rendered text). */
  lines: Array<{ key: string; params: Record<string, string | number> }>;
  /** A daily/weekly cap clipped this grant (progression §18.2). */
  capped?: boolean;
  /** Held «Отложено до утра» (e.g. a 3rd order quest; GDD N18). */
  held?: boolean;
}

// ═════════════════════════════════════════════════════════════════════════════════════════════════════════════════════
// §3.7 Services (cross-feature calls) — minimal P0 surfaces; owners may extend through a contract PR
// ═════════════════════════════════════════════════════════════════════════════════════════════════════════════════════

export interface Services {
  /** Provided by meta in onBoot. */
  rewards: RewardService;
  /** Meta. */
  economy: EconomyService;
  /** Meta-owned validated inventory and deterministic combat build. Optional on legacy/headless mocks. */
  equipment?: import('./equipment').EquipmentService;
  /** Creature. */
  creatures: CreatureService;
  /** Combat. */
  combat: CombatService;
  /** Cards. */
  cards: CardService;
  /** Explore. */
  explore: ExploreService;
}
export interface RewardService {
  /** The single reward pipeline (progression-economy.md §18): `grant('arena.win', { arena: fact })`. */
  grant(source: RewardId, bundle: RewardBundle, ctx?: { creatureId?: string }): GrantResult;
  /** E.g. cards registers 'card'. */
  registerSink(kind: string, sink: (store: Store, amount: number, ref?: string) => void): void;
}
export interface EconomyService { balance(currency: string): number; spend(currency: string, amount: number, reason: string): boolean; level(creatureId: string): number }
export interface CreatureService {
  /** Full saved GLB recipe, independent from the gameplay DNA. */
  appearance(id:string): import('./atelier').AtelierAppearance | null;
  createAtelierActor(appearance: import('./atelier').AtelierAppearance): Promise<import('./atelier-render').AtelierActorRuntime>;
  atelierPreset(model:string, outfit:string, name:string): Promise<import('./atelier').AtelierAppearance>;
  /** Species-correct DNA for procedural NPCs, independent of the player's anatomy. */
  createPreset(species: SpeciesId, opts: { name: string; soulSeed: number; look?: number }): CreatureDNA;
  /** Code-generated portrait of the real DNA mesh; optional on headless service mocks. */
  portrait?(dna: CreatureDNA): { canvas: HTMLCanvasElement; set(dna: CreatureDNA): void; render(ms: number): void; dispose(): void };
  active(): { id: string; dna: CreatureDNA } | null; get(id: string): { id: string; dna: CreatureDNA } | null;
  /** The animation runtime for other features' presentation layers (combat director, explore render). */
  readonly anim: AnimService;
}
export interface AnimService {
  readonly clips: ClipLibrary;
  /** Route-level clip preload (§5.11). */
  loadFamilies(families: readonly ClipFamily[]): Promise<void>;
  createWorld(opts: { seed: string; reducedMotion: boolean; startMs?: number }): AnimWorldApi;
  /** Rig + mesh + look for rendering; cached by (DNA code, lod, context, scale). */
  prepare(dna: CreatureDNA, opts: { lod: 0 | 1 | 'hero'; context: 'battle' | 'showcase'; shadowTint: [number, number, number]; scale?: number }): { rig: Rig; mesh: CreatureMeshData; look: CreatureLookData };
  preparePet(petId: string, opts: { lod: 0 | 1 }): { rig: Rig; mesh: CreatureMeshData; look: CreatureLookData };
  prepareBoss(bossId: 'ink_squid' | 'great_snailord', opts: { lod: 0 | 1; shadowTint: [number, number, number] }): { rig: Rig; mesh: CreatureMeshData; look: CreatureLookData; props: PropMeshData[] };
  /** Creatures §C6.5 / §C7; times from world.rate/clipRealDuration. `group` = CombatEvent[] (combat §4.10, P3-M1). */
  planGroup(group: readonly unknown[] /* CombatEvent[] */, stage: StageState, world: AnimWorldApi): BeatPlan;
  /** Creatures §C7.7 (shield omen opens before the swing exists). */
  predictClass(weapon: WeaponMeta | null): AnimClass;
  /** Creatures §C3.14; pure, replay-exact. */
  pickVariant(input: VariantInput): VariantPick;
}
export interface CombatService {
  /** Full deterministic auto-run (exploration boss fights, ghosts, balance). `spec` = FightSpec (combat.md §4.10, P3-M1). */
  runAuto(spec: unknown /* FightSpec, combat.md §4.10 */): { winner: 0 | 1; reason: 'ko' | 'judges'; events: number; hpLeft: [number, number] };
  buildSpec(creatureId: string, opts: Record<string, unknown>): unknown;
}
export interface CardService { addCards(cardIds: string[], reason: string): void }
export interface ExploreService { summaryForDashboard(): { regionKey: string; stateKey: string; lastLines: string[] } }

// ═════════════════════════════════════════════════════════════════════════════════════════════════════════════════════
// Input (§7; implemented in src/app/input.ts) and the app context
// ═════════════════════════════════════════════════════════════════════════════════════════════════════════════════════

/** Semantic actions (keyboard: Enter/Space confirm, Esc/Backspace back, 1–4 prayers, ←/→ prev/next). */
export type InputAction = 'confirm' | 'back' | 'next' | 'prev' | 'prayer1' | 'prayer2' | 'prayer3' | 'prayer4' | 'omenTap' | 'pause' | 'skip';
export interface PointerInfo { x: number; y: number; dx: number; dy: number; scale: number; target: EventTarget | null; timeStamp: number }
export interface Input {
  /** presentMs = the displayed instant at event.timeStamp (omen judging, §7). */
  on(action: InputAction, fn: (e: { presentMs: number; simMs: number; timeStamp: number }) => void): () => void;
  /** Long-press = 400 ms everywhere (GDD C20). */
  onPointer(kind: 'tap' | 'longPress' | 'drag' | 'pinch', fn: (e: PointerInfo) => void): () => void;
  endFrame(): void;
}

/** Everything a feature receives at boot and every screen receives (as `ScreenContext`). */
export interface AppContext {
  store: Store; bus: EventBus<GameEvents>; clock: Clock; router: Router; services: Services;
  /** null = WebGPU unavailable → lite mode «Без миниатюр» (§11, GDD C185). */
  renderer: Renderer | null;
  input: Input; t: TFunction;
  viewport: ReadonlySignal<Viewport>; debug: boolean; rootRng: Rng;
}

// ═════════════════════════════════════════════════════════════════════════════════════════════════════════════════════
// Debug ids shared across owners (architecture §8.1; GDD §4.15; creatures §D2)
// ═════════════════════════════════════════════════════════════════════════════════════════════════════════════════════

/** GDD §4.15 save-fixture union list (19): `src/debug/fixtures/<id>.json`, `?fixture=<id>`. */
export const FIXTURE_IDS = ['fresh', 'tutorial', 'l3', 'early', 'mid', 'l12-rich', 'late', 'away-8h', 'pending-decisions',
  'full-chest', 'no-candles', 'order-claims', 'day-claims', 'strong-build', 'hand-10', 'grudge',
  'slice-d2', 'slice-d3', 'slice-cap'] as const;
export type FixtureId = (typeof FIXTURE_IDS)[number];
/** creatures §D2 «Кунсткамера» DNA fixtures (B1 squid, B2 Snailord). */
export type KunstId = `K${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12}` | 'B1' | 'B2';
